import { useEffect, useState, useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Activity,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  Gauge,
  Link2,
  Search,
  FileText,
  Mail,
  CheckCircle2,
  XCircle,
  Minus,
  Play,
  ChevronRight,
  ListTree,
  Zap,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import type { Site } from './shared'
import {
  IssueBreakdown,
  CategoryView,
  PageSpeedOpportunities,
  parseSerankingIssues,
  parsePageSpeedOpportunities,
} from './seo'

interface SeoHealthTabProps {
  site: Site
}

interface SeoConfig {
  id: number
  siteDomain: string
  reportEnabled: boolean
  reportFrequency: string
  clientEmail: string | null
  clientPortalEnabled: boolean
  clientPortalToken: string | null
  createdAt: string
  updatedAt: string
}

interface SeoAudit {
  id: number
  siteDomain: string
  auditDate: string
  serankingAuditId: string | null
  serankingHealthScore: number | null
  serankingIssuesCritical: number | null
  serankingIssuesWarnings: number | null
  serankingIssuesNotices: number | null
  serankingBrokenLinks: number | null
  serankingPagesCrawled: number | null
  serankingRawData: string | null
  pagespeedPerformanceMobile: number | null
  pagespeedAccessibility: number | null
  pagespeedSeo: number | null
  pagespeedBestPractices: number | null
  pagespeedLcpMobile: number | null
  pagespeedClsMobile: number | null
  pagespeedFcpMobile: number | null
  pagespeedPerformanceDesktop: number | null
  pagespeedRawData: string | null
}

interface CoreWebVitals {
  lcp: string
  fid: string
  cls: string
  fcp: string
  rating: 'good' | 'needs-improvement' | 'poor'
}

const scoreColorClass = (score: number | null): string => {
  if (score === null) return 'text-muted-foreground'
  if (score >= 90) return 'text-green-500'
  if (score >= 50) return 'text-yellow-500'
  return 'text-red-500'
}

const scoreBgClass = (score: number | null): string => {
  if (score === null) return 'bg-muted'
  if (score >= 90) return 'bg-green-500/10'
  if (score >= 50) return 'bg-yellow-500/10'
  return 'bg-red-500/10'
}

const ratingIcon = (rating: string | null) => {
  if (rating === 'good') return <CheckCircle2 className="h-4 w-4 text-green-500" />
  if (rating === 'needs-improvement') return <Minus className="h-4 w-4 text-yellow-500" />
  if (rating === 'poor') return <XCircle className="h-4 w-4 text-red-500" />
  return <Minus className="h-4 w-4 text-muted-foreground" />
}

export function SeoHealthTab({ site }: SeoHealthTabProps) {
  const [_config, setConfig] = useState<SeoConfig | null>(null)
  const [audit, setAudit] = useState<SeoAudit | null>(null)
  const [cwv, setCwv] = useState<CoreWebVitals | null>(null)
  const [loading, setLoading] = useState(true)
  const [triggering, setTriggering] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state for config
  const [formEmail, setFormEmail] = useState('')
  const [formReportEnabled, setFormReportEnabled] = useState(true)

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch config
      const configRes = await fetch(`/api/seo/config/${encodeURIComponent(site.domain)}`)
      if (configRes.ok) {
        const configData = (await configRes.json()) as SeoConfig
        setConfig(configData)
        setFormEmail(configData.clientEmail || '')
        setFormReportEnabled(configData.reportEnabled ?? true)
      } else if (configRes.status === 404) {
        setConfig(null)
      }

      // Fetch latest audit
      const auditRes = await fetch(`/api/seo/audit/${encodeURIComponent(site.domain)}/latest`)
      if (auditRes.ok) {
        const auditData = (await auditRes.json()) as { audit: SeoAudit; coreWebVitals: CoreWebVitals | null }
        setAudit(auditData.audit)
        setCwv(auditData.coreWebVitals)
      } else if (auditRes.status === 404) {
        setAudit(null)
        setCwv(null)
      }
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const triggerAudit = async () => {
    setTriggering(true)
    try {
      const res = await fetch(`/api/seo/audit/${encodeURIComponent(site.domain)}/trigger`, {
        method: 'POST',
      })
      if (!res.ok) throw new Error('Failed to trigger audit')

      // Refetch after a short delay to get PageSpeed results
      setTimeout(fetchData, 2000)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setTriggering(false)
    }
  }

  const saveConfig = async () => {
    setSaving(true)
    try {
      const res = await fetch(`/api/seo/config/${encodeURIComponent(site.domain)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reportEnabled: formReportEnabled,
          clientEmail: formEmail || null,
        }),
      })
      if (!res.ok) throw new Error('Failed to save config')
      const newConfig = (await res.json()) as SeoConfig
      setConfig(newConfig)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [site.domain])

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border p-4 space-y-3">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-20 w-full" />
        </div>
        <div className="rounded-lg border p-4 space-y-3">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border p-6 text-center">
        <AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Failed to load SEO data: {error}</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Search className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">SEO Health</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm" onClick={triggerAudit} disabled={triggering}>
            <Play className="h-4 w-4 mr-2" />
            {triggering ? 'Running...' : 'Run Audit'}
          </Button>
        </div>
      </div>

      {/* No Audit Yet */}
      {!audit && (
        <div className="rounded-lg border p-6 text-center">
          <Activity className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="font-medium">No SEO audits yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Run your first audit to see PageSpeed and SE Ranking data.
          </p>
          <Button className="mt-4" onClick={triggerAudit} disabled={triggering}>
            <Play className="h-4 w-4 mr-2" />
            {triggering ? 'Running...' : 'Run First Audit'}
          </Button>
        </div>
      )}

      {/* Audit Results */}
      {audit && (
        <>
          {/* PageSpeed Scores Card */}
          <div className="rounded-lg border p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-medium">
                <Gauge className="h-4 w-4 text-muted-foreground" />
                PageSpeed Insights (Mobile)
              </div>
              <Badge variant="outline">
                {audit.auditDate
                  ? formatDistanceToNow(new Date(audit.auditDate), { addSuffix: true })
                  : 'Unknown'}
              </Badge>
            </div>

            {/* Score Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <ScoreCard
                label="Performance"
                score={audit.pagespeedPerformanceMobile}
                icon={<Gauge className="h-5 w-5" />}
              />
              <ScoreCard
                label="Accessibility"
                score={audit.pagespeedAccessibility}
                icon={<Activity className="h-5 w-5" />}
              />
              <ScoreCard
                label="SEO"
                score={audit.pagespeedSeo}
                icon={<Search className="h-5 w-5" />}
              />
              <ScoreCard
                label="Best Practices"
                score={audit.pagespeedBestPractices}
                icon={<FileText className="h-5 w-5" />}
              />
            </div>

            {/* Core Web Vitals */}
            {cwv && (
              <div className="pt-4 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">Core Web Vitals</span>
                  {ratingIcon(cwv.rating)}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard label="LCP" value={cwv.lcp} description="Largest Contentful Paint" />
                  <MetricCard label="FID" value={cwv.fid} description="First Input Delay" />
                  <MetricCard label="CLS" value={cwv.cls} description="Cumulative Layout Shift" />
                  <MetricCard label="FCP" value={cwv.fcp} description="First Contentful Paint" />
                </div>
              </div>
            )}

            {/* Desktop Score */}
            {audit.pagespeedPerformanceDesktop !== null && (
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Desktop Performance</span>
                  <span className={`text-lg font-semibold ${scoreColorClass(audit.pagespeedPerformanceDesktop)}`}>
                    {audit.pagespeedPerformanceDesktop}
                  </span>
                </div>
              </div>
            )}

            {/* PageSpeed Opportunities */}
            <PageSpeedOpportunitiesSection rawData={audit.pagespeedRawData} />
          </div>

          {/* SE Ranking Card */}
          {audit.serankingHealthScore !== null && (
            <SeRankingCard audit={audit} />
          )}

          {/* No SE Ranking Data */}
          {audit.serankingHealthScore === null && (
            <div className="rounded-lg border p-4">
              <div className="flex items-center gap-2 font-medium mb-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                SE Ranking Site Audit
              </div>
              <p className="text-sm text-muted-foreground">
                {audit.serankingAuditId
                  ? 'SE Ranking audit is processing. Refresh in a few minutes to see results.'
                  : 'Run an audit to get SE Ranking site health data.'}
              </p>
            </div>
          )}
        </>
      )}

      {/* Report Settings Card */}
      <div className="rounded-lg border p-4 space-y-4">
        <div className="flex items-center gap-2 font-medium">
          <Mail className="h-4 w-4 text-muted-foreground" />
          Report Settings
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="report-enabled">Monthly Reports</Label>
              <p className="text-sm text-muted-foreground">
                Send automated SEO health reports to the client
              </p>
            </div>
            <Switch
              id="report-enabled"
              checked={formReportEnabled}
              onCheckedChange={setFormReportEnabled}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="client-email">Client Email</Label>
            <Input
              id="client-email"
              type="email"
              placeholder="client@example.com"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Reports will be sent to this email address
            </p>
          </div>

          <Button onClick={saveConfig} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// Helper Components

function ScoreCard({
  label,
  score,
  icon,
}: {
  label: string
  score: number | null
  icon: React.ReactNode
}) {
  return (
    <div className={`p-3 rounded-lg ${scoreBgClass(score)}`}>
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${scoreColorClass(score)}`}>
        {score ?? '-'}
      </div>
    </div>
  )
}

function MetricCard({
  label,
  value,
  description,
}: {
  label: string
  value: string
  description: string
}) {
  return (
    <div className="p-2 rounded border bg-card">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span className="font-mono text-sm">{value}</span>
      </div>
      <p className="text-xs text-muted-foreground mt-1 truncate" title={description}>
        {description}
      </p>
    </div>
  )
}

function IssueCard({
  label,
  count,
  variant,
  icon,
}: {
  label: string
  count: number | null
  variant: 'destructive' | 'warning' | 'default'
  icon?: React.ReactNode
}) {
  const bgClass =
    variant === 'destructive'
      ? 'bg-red-500/10'
      : variant === 'warning'
      ? 'bg-yellow-500/10'
      : 'bg-muted'

  const textClass =
    variant === 'destructive'
      ? 'text-red-500'
      : variant === 'warning'
      ? 'text-yellow-500'
      : 'text-muted-foreground'

  return (
    <div className={`p-3 rounded-lg ${bgClass} text-center`}>
      {icon && <div className={`${textClass} mb-1 flex justify-center`}>{icon}</div>}
      <div className={`text-xl font-bold ${textClass}`}>{count ?? 0}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  )
}

/**
 * PageSpeed Opportunities Section - expandable view of improvement opportunities
 */
function PageSpeedOpportunitiesSection({ rawData }: { rawData: string | null }) {
  const [open, setOpen] = useState(false)

  const opportunities = useMemo(
    () => parsePageSpeedOpportunities(rawData),
    [rawData]
  )

  // Don't show if no opportunities or no raw data
  if (!rawData || opportunities.length === 0) {
    return null
  }

  return (
    <div className="pt-4 border-t">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full py-1">
          <ChevronRight
            className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`}
          />
          <Zap className="h-4 w-4" />
          <span>View Opportunities ({opportunities.length})</span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3">
          <PageSpeedOpportunities opportunities={opportunities} />
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

/**
 * SE Ranking Card with expandable issue details
 */
function SeRankingCard({ audit }: { audit: SeoAudit }) {
  const [issuesOpen, setIssuesOpen] = useState(false)
  const [categoryOpen, setCategoryOpen] = useState(false)

  // Parse raw data for detailed views
  const parsedIssues = useMemo(
    () => parseSerankingIssues(audit.serankingRawData),
    [audit.serankingRawData]
  )

  const hasDetailedData = parsedIssues !== null

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div className="flex items-center gap-2 font-medium">
        <Activity className="h-4 w-4 text-muted-foreground" />
        SE Ranking Site Audit
      </div>

      {/* Health Score */}
      <div className="flex items-center gap-4">
        <div className={`p-4 rounded-full ${scoreBgClass(audit.serankingHealthScore)}`}>
          <span className={`text-3xl font-bold ${scoreColorClass(audit.serankingHealthScore)}`}>
            {audit.serankingHealthScore}
          </span>
        </div>
        <div>
          <div className="text-lg font-medium">Health Score</div>
          <div className="text-sm text-muted-foreground">
            {audit.serankingPagesCrawled} pages crawled
          </div>
        </div>
      </div>

      {/* Issues Grid */}
      <div className="grid grid-cols-4 gap-3 pt-2 border-t">
        <IssueCard
          label="Critical"
          count={audit.serankingIssuesCritical}
          variant="destructive"
        />
        <IssueCard
          label="Warnings"
          count={audit.serankingIssuesWarnings}
          variant="warning"
        />
        <IssueCard
          label="Notices"
          count={audit.serankingIssuesNotices}
          variant="default"
        />
        <IssueCard
          label="Broken Links"
          count={audit.serankingBrokenLinks}
          variant="destructive"
          icon={<Link2 className="h-4 w-4" />}
        />
      </div>

      {/* Expandable Issue Details */}
      {hasDetailedData && (
        <div className="pt-2 border-t space-y-2">
          {/* View Issue Details */}
          <Collapsible open={issuesOpen} onOpenChange={setIssuesOpen}>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full py-1">
              <ChevronRight
                className={`h-4 w-4 transition-transform ${issuesOpen ? 'rotate-90' : ''}`}
              />
              <AlertTriangle className="h-4 w-4" />
              <span>View Issue Details</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <IssueBreakdown issues={parsedIssues} domain={audit.siteDomain} />
            </CollapsibleContent>
          </Collapsible>

          {/* View by Category */}
          <Collapsible open={categoryOpen} onOpenChange={setCategoryOpen}>
            <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full py-1">
              <ChevronRight
                className={`h-4 w-4 transition-transform ${categoryOpen ? 'rotate-90' : ''}`}
              />
              <ListTree className="h-4 w-4" />
              <span>View by Category</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <CategoryView issues={parsedIssues} />
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  )
}
